/**
 * SUNDAY FLOOR — main-collection.js
 * Filter drawer as a focus-trapped dialog (same pattern as the menu drawer),
 * plus filter/sort updates via the Section Rendering API so applying a
 * filter or changing sort never triggers a full page reload. Falls back to
 * ordinary navigation (the form's plain GET action, the pills' plain hrefs)
 * if anything here fails — no behavior is JS-only.
 *
 * State rules:
 * - Every fetch this module makes is built from the CURRENT URL (which
 *   always reflects the last-applied filters/sort, either from the initial
 *   page load or from a previous pushState here), so sorting never drops
 *   active filters and filtering never drops the active sort.
 * - The Section Rendering `section_id` param uses the section's real
 *   rendered id (data-section-id), not the section type.
 * - Every history entry pushed here is restorable: popstate re-fetches the
 *   URL the browser has just navigated to and re-renders from it, so
 *   filter -> sort -> Back returns both the URL and the rendered grid to
 *   the prior state.
 */
import { trapFocus, setBackgroundInert } from './global.js';

// Module-level (not per-app-instance) since a collection page only ever
// has one [data-collection-app] — see fetchAndReplace's stale-response
// protection below.
let collectionRequestId = 0;
let activeCollectionController = null;

document.querySelectorAll('[data-collection-app]').forEach(initCollectionApp);

window.addEventListener('popstate', () => {
  const app = document.querySelector('[data-collection-app]');
  if (!app) return;
  fetchAndReplace(app, new URL(window.location.href), { updateHistory: false });
});

function initCollectionApp(app) {
  const drawer = app.querySelector('[data-filter-drawer]');
  const drawerControls = drawer ? initFilterDrawer(drawer) : null;
  app._sfDrawerControls = drawerControls;

  const sortSelect = app.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      // Base off the current URL (already carries any active filters),
      // not the bare collection URL, so filters survive a sort change.
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      fetchAndReplace(app, url);
    });
  }

  const facetForm = app.querySelector('[data-facet-form]');
  if (facetForm) {
    facetForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const url = new URL(facetForm.action, window.location.origin);
      url.search = new URLSearchParams(new FormData(facetForm)).toString();
      // sort_by lives in the toolbar, not the facet form, so carry the
      // current sort selection forward explicitly.
      if (sortSelect) url.searchParams.set('sort_by', sortSelect.value);
      fetchAndReplace(app, url);
      if (drawerControls) drawerControls.close();
    });
  }

  app.querySelectorAll('[data-remove-filter]').forEach((pill) => {
    pill.addEventListener('click', (e) => {
      e.preventDefault();
      fetchAndReplace(app, new URL(pill.href, window.location.origin));
    });
  });

  const clearLink = app.querySelector('[data-clear-filters]');
  if (clearLink) {
    clearLink.addEventListener('click', (e) => {
      e.preventDefault();
      // "Clear filters" clears filters, not sort — carry sort_by forward.
      const url = new URL(clearLink.href, window.location.origin);
      if (sortSelect) url.searchParams.set('sort_by', sortSelect.value);
      fetchAndReplace(app, url);
    });
  }
}

function initFilterDrawer(drawer) {
  const openTrigger = document.querySelector(`[aria-controls="${drawer.id}"]`);
  const closeTargets = drawer.querySelectorAll('[data-filter-close]');

  const state = { removeTrap: () => {}, lastFocused: null };

  function open() {
    state.lastFocused = document.activeElement;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    setBackgroundInert(drawer, true);
    state.removeTrap = trapFocus(drawer);
    const firstFocusable = drawer.querySelector('button, a, input');
    if (firstFocusable) firstFocusable.focus();
    document.addEventListener('keydown', onKeydown);
  }

  // The single close routine every close path uses — close button, scrim,
  // Escape, and "Apply" on the facet form — so none of them can leave the
  // focus trap, the Escape listener, or the inert background half-applied.
  function close() {
    if (drawer.hidden) return;
    drawer.hidden = true;
    document.body.style.overflow = '';
    setBackgroundInert(drawer, false);
    state.removeTrap();
    document.removeEventListener('keydown', onKeydown);
    if (state.lastFocused) state.lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  if (openTrigger) openTrigger.addEventListener('click', open);
  closeTargets.forEach((el) => el.addEventListener('click', close));

  return { open, close };
}

async function fetchAndReplace(app, url, { updateHistory = true } = {}) {
  // Stale-response protection: a rapid sort change followed immediately by
  // a filter change (or vice versa) can leave two requests in flight at
  // once. Without this, whichever happened to resolve second would win
  // regardless of which was actually the user's most recent action.
  const requestId = ++collectionRequestId;
  if (activeCollectionController) activeCollectionController.abort();
  activeCollectionController = new AbortController();

  const sectionId = app.dataset.sectionId;
  url.searchParams.set('section_id', sectionId);

  // Force-close the drawer before the node it belongs to is removed, so the
  // document-level Escape listener and inert state never outlive the DOM
  // they were attached for, regardless of which control triggered this.
  if (app._sfDrawerControls) app._sfDrawerControls.close();

  try {
    const response = await fetch(url.toString(), { signal: activeCollectionController.signal });
    if (requestId !== collectionRequestId) return; // superseded by a newer request
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const html = await response.text();
    if (requestId !== collectionRequestId) return; // superseded while awaiting the body

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newApp = doc.querySelector('[data-collection-app]');
    if (!newApp) throw new Error('Updated section markup not found');

    app.replaceWith(newApp);
    initCollectionApp(newApp);

    // The count is aria-live="polite", so screen readers announce the new
    // total automatically. Deliberately not moving focus here — the person
    // may still be mid-interaction with the sort select or the filter
    // drawer's Apply button, and yanking focus away from that would be more
    // disorienting than helpful. Nothing in the accessibility spec (Part I)
    // requires a focus move for this update, only the live-region announcement.

    url.searchParams.delete('section_id');
    if (updateHistory) {
      window.history.pushState({}, '', url.toString());
    }
  } catch (err) {
    if (err.name === 'AbortError') return; // superseded, not a real failure
    if (requestId !== collectionRequestId) return;
    // Progressive-enhancement fallback: if the fetch/update fails for any
    // reason, do a normal navigation instead of leaving the page stuck or
    // the URL and DOM out of sync.
    url.searchParams.delete('section_id');
    window.location.href = url.toString();
  }
}
